import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function CallToAction() {
  const videoRef = useRef<HTMLDivElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [annualSpend, setAnnualSpend] = useState(3300.0);
  // Slideshow state
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideshowRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Product selection state - Changed default to 'bundle'
  const [selectedProduct, setSelectedProduct] = useState<'oculis' | 'bundle'>('bundle');
  // Slideshow images
  const slideshowImages = [{
    url: 'https://cdn.shopify.com/s/files/1/0679/7135/0570/files/Gemini_Generated_Image_aldfd5aldfd5aldf.png?v=1761010116',
    alt: 'Cost comparison: $13,897 in traditional treatments vs $94.99 one-time purchase for Oculis-3 device'
  }, {
    url: 'https://cdn.shopify.com/s/files/1/0679/7135/0570/files/unnamed_15.jpg?v=1761011011',
    alt: 'The Recovery Bundle - Oculis-3 device with carrying case, charging cable and accessories - $149.99 One-Time'
  }];
  // Functions to control slideshow
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % slideshowImages.length);
  const prevSlide = () => setCurrentSlide(prev => prev === 0 ? slideshowImages.length - 1 : prev - 1);
  const goToSlide = (index: number) => setCurrentSlide(index);
  // Auto-rotate slideshow
  useEffect(() => {
    // Start auto-rotation
    slideshowRef.current = setInterval(nextSlide, 5000);
    return () => {
      if (slideshowRef.current) clearInterval(slideshowRef.current);
    };
  }, []);
  // Reset timer when manually changing slide
  useEffect(() => {
    if (slideshowRef.current) {
      clearInterval(slideshowRef.current);
      slideshowRef.current = setInterval(nextSlide, 5000);
    }
  }, [currentSlide]);
  // Lazy load the video thumbnail and prepare for autoplay
  useEffect(() => {
    if (!videoRef.current) return;
    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };
    const loadVideoThumbnail = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && videoRef.current && !videoLoaded) {
        const img = document.createElement('img');
        img.src = 'https://img.youtube.com/vi/xWP3071arcA/maxresdefault.jpg';
        img.alt = 'Oculis 3 Eye Therapy Device Video Thumbnail';
        img.className = 'absolute top-0 left-0 w-full h-full object-cover hover-zoom';
        img.width = 640;
        img.height = 360;
        img.loading = 'lazy';
        img.decoding = 'async';
        const playButton = document.createElement('div');
        playButton.className = 'absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer';
        const playIcon = document.createElement('div');
        playIcon.className = 'bg-white bg-opacity-80 text-teal-600 rounded-full p-4 shadow-lg';
        playIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>`;
        playButton.appendChild(playIcon);
        playButton.addEventListener('click', () => {
          if (!videoRef.current) return;
          videoRef.current.innerHTML = '';
          const iframe = document.createElement('iframe');
          iframe.className = 'absolute top-0 left-0 w-full h-full';
          iframe.src = 'https://www.youtube.com/embed/xWP3071arcA?autoplay=1&mute=0&controls=1&disablekb=0&fs=0&modestbranding=1&rel=0&showinfo=0&loop=1&playlist=xWP3071arcA&iv_load_policy=3';
          iframe.title = 'Oculis 3 Eye Therapy Device Video';
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.frameBorder = '0';
          iframe.loading = 'lazy';
          videoRef.current.appendChild(iframe);
        });
        if (videoRef.current) {
          videoRef.current.appendChild(img);
          videoRef.current.appendChild(playButton);
          setVideoLoaded(true);
        }
        observer.disconnect();
      }
    };
    const observer = new IntersectionObserver(loadVideoThumbnail, options);
    observer.observe(videoRef.current);
    return () => {
      observer.disconnect();
    };
  }, [videoLoaded]);
  // Handle order now
  const handleOrderNow = () => {
    const url = selectedProduct === 'oculis' ? 'https://irelief.us/cart/43727274508330:1' : 'https://irelief.us/cart/43727255437354:1';
    window.location.href = url;
  };
  return <section id="call-to-action-section" className="py-12 sm:py-16 md:py-20" data-id="element-44">
      <div className="bg-gradient-to-r from-teal-500 to-blue-500 rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12 text-white text-center reveal-on-scroll gradient-animation shadow-2xl" data-id="element-45">
        {/* Main Headline */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-6 md:mb-6 text-white-900 tracking-tight" data-id="element-46">
          🍁 Fall Into Comfort Sale 🍂
        </h2>
        {/* Main Content Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 p-6 sm:p-2 md:p-2 mb-2 max-w-3xl mx-auto backdrop-blur-sm" data-id="element-47">
          {/* Offer Presentation Container */}
          <div className="rounded-2xl overflow-hidden mb-6 sm:mb-8 shadow-lg border border-gray-200" data-id="element-48">
            {/* Header */}
            <div className="w-full bg-gradient-to-r from-teal-500 to-blue-500 py-4 sm:py-5 px-4 sm:px-6 text-center" data-id="element-49">
              <span className="text-white text-sm sm:text-base md:text-lg font-semibold tracking-wide" data-id="element-50">
                Choose Your Option
              </span>
            </div>
            {/* Product Options */}
            <div className="bg-white" data-id="element-51">
              {/* OCULIS-3 Option */}
              <div className={`border-b border-gray-200 p-5 sm:p-6 md:p-8 cursor-pointer transition-all duration-300 ease-in-out ${selectedProduct === 'oculis' ? 'bg-gradient-to-r from-blue-50 to-teal-50 shadow-inner' : 'bg-white hover:bg-gray-50'}`} onClick={() => setSelectedProduct('oculis')} data-id="element-52">
                <div className="flex items-start gap-4 sm:gap-5" data-id="element-53">
                  {/* Radio Button */}
                  <div className="flex items-center pt-1 flex-shrink-0" data-id="element-54">
                    <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${selectedProduct === 'oculis' ? 'border-teal-500 bg-teal-500 shadow-md' : 'border-gray-300 bg-white'}`} data-id="element-55">
                      {selectedProduct === 'oculis' && <div className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-white" data-id="element-56"></div>}
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-57">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4" data-id="element-58">
                      <div className="flex-1" data-id="element-59">
                        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-1" data-id="element-60">
                          OCULIS-3
                        </h3>
                        <p className="text-xs sm:text-sm text-teal-600 font-medium" data-id="element-61">
                          Fall Sale Discount
                        </p>
                      </div>
                      <div className="flex justify-between items-center sm:block sm:text-right flex-shrink-0 mt-1 sm:mt-0" data-id="element-62">
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900" data-id="element-63">
                          $94.99
                        </div>
                        <div className="text-sm sm:text-base text-gray-400 line-through" data-id="element-64">
                          $149.99
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* The Recovery Bundle Option */}
              <div className={`p-5 sm:p-6 md:p-8 cursor-pointer transition-all duration-300 ease-in-out ${selectedProduct === 'bundle' ? 'bg-gradient-to-r from-blue-50 to-teal-50 shadow-inner' : 'bg-white hover:bg-gray-50'}`} onClick={() => setSelectedProduct('bundle')} data-id="element-65">
                <div className="flex items-start gap-4 sm:gap-5" data-id="element-66">
                  {/* Radio Button */}
                  <div className="flex items-center pt-1 flex-shrink-0" data-id="element-67">
                    <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${selectedProduct === 'bundle' ? 'border-teal-500 bg-teal-500 shadow-md' : 'border-gray-300 bg-white'}`} data-id="element-68">
                      {selectedProduct === 'bundle' && <div className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-white" data-id="element-69"></div>}
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-70">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4 mb-4" data-id="element-71">
                      <div className="flex-1" data-id="element-72">
                        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-1" data-id="element-73">
                          The Recovery Bundle
                        </h3>
                        <p className="text-xs sm:text-sm text-teal-600 font-medium" data-id="element-74">
                          Original Price + Free Essentials
                        </p>
                      </div>
                      <div className="flex justify-between items-center sm:block sm:text-right flex-shrink-0 mt-1 sm:mt-0" data-id="element-75">
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900" data-id="element-76">
                          $149.99
                        </div>
                        <div className="text-sm sm:text-base text-gray-400 line-through" data-id="element-77">
                          $259.99
                        </div>
                      </div>
                    </div>
                    {/* Bundle Items with Image Containers */}
                    {selectedProduct === 'bundle' && <div className="mt-5 sm:mt-6 grid grid-cols-3 gap-3 sm:gap-4 md:gap-5" data-id="element-78">
                        {/* OCULIS-3 */}
                        <div className="text-center" data-id="element-79">
                          <div className="w-full aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center border border-gray-200 mb-2 sm:mb-3 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200" data-id="element-80">
                            <img src="https://cdn.shopify.com/s/files/1/0679/7135/0570/files/Gemini_Generated_Image_8c9giu8c9giu8c9g.png?v=1760993650" alt="OCULIS-3" className="h-full w-auto object-cover" data-id="element-81" />
                          </div>
                          <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-1" data-id="element-82">
                            OCULIS-3
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600" data-id="element-83">
                            $149.99
                          </div>
                        </div>
                        {/* NEBRI Mist */}
                        <div className="text-center" data-id="element-84">
                          <div className="w-full aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center border border-gray-200 mb-2 sm:mb-3 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200" data-id="element-85">
                            <img src="https://cdn.shopify.com/s/files/1/0679/7135/0570/files/Untitled_design_8.png?v=1760994179" alt="Nebri Mist" className="h-full w-auto object-cover" data-id="element-86" />
                          </div>
                          <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-1" data-id="element-87">
                            NEBRI Mist
                          </div>
                          <div className="text-xs sm:text-sm text-teal-600 font-semibold" data-id="element-88">
                            Free
                          </div>
                          <div className="text-xs text-gray-400 line-through" data-id="element-89">
                            $90.00
                          </div>
                        </div>
                        {/* Travel Case */}
                        <div className="text-center" data-id="element-90">
                          <div className="w-full aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center border border-gray-200 mb-2 sm:mb-3 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200" data-id="element-91">
                            <img src="https://cdn.shopify.com/s/files/1/0679/7135/0570/files/797f367e-cc40-44ec-9288-01d4c305f893.png?v=1760992715" alt="Travel Case" className="w-full h-full object-cover" data-id="element-92" />
                          </div>
                          <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-1" data-id="element-93">
                            Travel Case
                          </div>
                          <div className="text-xs sm:text-sm text-teal-600 font-semibold" data-id="element-94">
                            FREE
                          </div>
                          <div className="text-xs text-gray-400 line-through" data-id="element-95">
                            $19.99
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
                {/* Recovery Bundle Explanation */}
                {selectedProduct === 'bundle' && <div className="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-gray-200" data-id="element-96">
                    <div className="bg-gradient-to-br from-teal-50 via-blue-50 to-teal-50 p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl border border-teal-100 shadow-sm" data-id="element-97">
                      <h4 className="text-sm sm:text-base font-semibold text-teal-800 mb-3 sm:mb-4 flex items-center" data-id="element-98">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-99">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" data-id="element-100" />
                        </svg>
                        Why This Bundle Works Better Together
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-700 mb-2 sm:mb-3 leading-relaxed" data-id="element-101">
                        <span className="font-semibold" data-id="element-102">Oculis-3</span> works
                        from the inside out — melting and expressing blocked
                        oils to fix the root cause.
                      </p>
                      <p className="text-xs sm:text-sm text-gray-700 mb-2 sm:mb-3 leading-relaxed" data-id="element-103">
                        <span className="font-semibold" data-id="element-104">Nebri Nebulizer</span>{' '}
                        soothes the surface with an ultra-fine hydrating mist
                        for immediate comfort.
                      </p>
                      <p className="text-xs sm:text-sm text-teal-700 font-medium leading-relaxed" data-id="element-105">
                        Together: Oculis-3 fixes the plumbing & Nebri calms the
                        storm while repairs happen. Perfect for harsh AC
                        seasons, allergy flare-ups, or days when your eyes need
                        extra TLC because your in the process of reducing daily
                        drop intake.
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
            {/* Order Now Button */}
            <div className="bg-white p-5 sm:p-6 md:p-8" data-id="element-106">
              <button onClick={handleOrderNow} className="w-full bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white font-semibold py-4 sm:py-5 px-6 sm:px-8 rounded-xl sm:rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-base sm:text-lg pulse-animation" data-id="element-107">
                Order Now
              </button>
            {/* Badge Row – Responsive, never overflows */}
            <div className="mt-2 w-full overflow-hidden" data-id="element-108">
  <div className="flex flex-wrap justify-center items-center gap-1.5 sm:gap-2" data-id="element-109">
    <div className="shrink-0 inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 sm:px-3 py-1 text-[11px] sm:text-xs" data-id="element-110">
      <span className="h-2 w-2 rounded-full bg-teal-600" data-id="element-111"></span>
      <span className="font-medium text-teal-700 whitespace-nowrap" data-id="element-112">HSA/FSA Eligible</span>
    </div>
    <div className="shrink-0 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 sm:px-3 py-1 text-[11px] sm:text-xs" data-id="element-113">
      <span className="h-2 w-2 rounded-full bg-emerald-600" data-id="element-114"></span>
      <span className="font-medium text-emerald-700 whitespace-nowrap" data-id="element-115">Up to 67% Off</span>
    </div>
       <div className="shrink-0 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 sm:px-3 py-1 text-[11px] sm:text-xs" data-id="element-116">
      <span className="h-2 w-2 rounded-full bg-blue-600" data-id="element-117"></span>
      <span className="font-medium text-blue-700 whitespace-nowrap" data-id="element-118">Daily At-Home Relief</span>
    </div>
  </div>
            </div>
              {/* 90-Day Guarantee Section */}
              <div className="mt-4 sm:mt-5 bg-gradient-to-br from-blue-50 to-teal-50 p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl border border-blue-100 shadow-sm" data-id="element-119">
                <div className="flex items-center justify-center mb-3" data-id="element-120">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-121">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-122" />
                  </svg>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 text-center" data-id="element-123">
                    90-Day Transformation Guarantee
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-gray-700 text-center leading-relaxed" data-id="element-124">
                  Try Oculis-3 for three full months. If you don't experience
                  dramatic improvement in your eye comfort and daily life, we'll
                  refund every penny. No questions, no hassle.
                </p>
              </div>
            </div>
          </div>
          {/* Compact Sale Details */}
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-400 p-4 sm:p-5 rounded-xl sm:rounded-2xl mb-6 sm:mb-8 shadow-sm" data-id="element-125">
            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed" data-id="element-126">
              <span className="font-semibold" data-id="element-127">Sale Details:</span> Due to
              overwhelming response from NBC's "Today" show, inventory is
              limited. We've secured enough units for the next 500 orders at
              $94.99 (reg. $149.99). After that, prices return to normal.
            </p>
          </div>
          {/* ROI Calculator Section */}
          <div className="mb-6 sm:mb-8 border-t border-gray-100 pt-8 sm:pt-10 mt-8 sm:mt-10" data-id="element-128">
            <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 tracking-tight" data-id="element-129">
              ROI Calculator
            </h4>
            {/* Slideshow Section */}
            <div className="w-full mb-4 sm:mb-6 relative" data-id="element-130">
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-xl relative" data-id="element-131">
                {/* Images */}
                {slideshowImages.map((image, index) => <div key={index} className={`w-full transition-opacity duration-500 ${currentSlide === index ? 'opacity-100 block' : 'opacity-0 hidden'}`} data-id="element-132">
                    <img src={image.url} alt={image.alt} className="w-full h-auto rounded-xl sm:rounded-2xl shadow-md" width="800" height="450" loading={index === 0 ? 'eager' : 'lazy'} data-id="element-133" />
                  </div>)}
                {/* Navigation arrows */}
                <button className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 sm:p-3 shadow-lg text-gray-800 transition-all z-10" onClick={prevSlide} aria-label="Previous slide" data-id="element-134">
                  <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-135" />
                </button>
                <button className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-2 sm:p-3 shadow-lg text-gray-800 transition-all z-10" onClick={nextSlide} aria-label="Next slide" data-id="element-136">
                  <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-137" />
                </button>
              </div>
              {/* Slide indicators */}
              <div className="flex justify-center mt-4 sm:mt-5" data-id="element-138">
                {slideshowImages.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`w-2.5 h-2.5 sm:w-3 sm:h-3 mx-1.5 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-teal-600 w-8 sm:w-10' : 'bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-139" />)}
              </div>
            </div>
            <div className="mb-6 sm:mb-8" data-id="element-140">
              <label className="block text-sm sm:text-base font-medium text-gray-700 mb-3 sm:mb-4" data-id="element-141">
                Enter your annual eye care expenses to see how much you could
                save with Oculis-3:
              </label>
              <div className="relative" data-id="element-142">
                <span className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 text-gray-700 text-lg sm:text-xl font-medium" data-id="element-143">
                  $
                </span>
                <input type="number" value={annualSpend} onChange={e => {
                const value = parseFloat(e.target.value) || 0;
                setAnnualSpend(value);
              }} className="w-full pl-10 sm:pl-12 pr-4 sm:pr-5 py-4 sm:py-5 border-2 border-gray-200 rounded-xl sm:rounded-2xl text-gray-900 text-base sm:text-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all shadow-sm" min="0" step="0.01" data-id="element-144" />
              </div>
              <div className="text-lg sm:text-xl md:text-2xl text-teal-600 font-bold mt-4 sm:mt-5" data-id="element-145">
                Oculis 3 pays for itself in{' '}
                {Math.max(1, Math.ceil(94.99 / ((annualSpend - 94.99) / 365)))}{' '}
                days
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm" data-id="element-146">
              <div className="flex justify-between items-center mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200" data-id="element-147">
                <span className="text-sm sm:text-base text-gray-700 font-medium" data-id="element-148">
                  Your annual cost:
                </span>
                <span className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-149">
                  $
                  {annualSpend.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </span>
              </div>
              <div className="flex justify-between items-center mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200" data-id="element-150">
                <span className="text-sm sm:text-base text-gray-700 font-medium" data-id="element-151">
                  iRelief Oculis-3:
                </span>
                <span className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-152">
                  $94.99
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 sm:pt-3" data-id="element-153">
                <span className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-154">
                  Annual savings:
                </span>
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-teal-600" data-id="element-155">
                  $
                  {Math.max(0, annualSpend - 94.99).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Video Section */}
        <div className="mb-6 max-w-3xl mx-auto reveal-on-scroll" data-id="element-156">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6" data-id="element-157">
            See It Featured on NBC's Today Show
          </h3>
          <div className="relative aspect-video w-full overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl" style={{
          minHeight: '200px',
          backgroundColor: '#000'
        }} ref={videoRef} data-id="element-158">
            {/* Video thumbnail and iframe will be injected here via IntersectionObserver */}
          </div>
        </div>
      </div>
    </section>;
}